/** Local favorites + light telemetry for Ask Talkie (no Backend). */

import { Injectable } from '@angular/core';

const FAV_KEY = 'askTalkie.favorites.v1';
const TEL_KEY = 'askTalkie.telemetry.v1';
const DETAIL_KEY = 'askTalkie.detailMode.v1';
const HIST_KEY = 'askTalkie.promptHistory.v1';

export type SlateDetailMode = 'short' | 'detailed';

@Injectable({ providedIn: 'root' })
export class SlatePrefsService {
    getFavorites(): string[] {
        return this.readJson<string[]>(FAV_KEY, []).filter(
            (x) => typeof x === 'string' && x.trim().length > 0
        );
    }

    toggleFavorite(prompt: string): string[] {
        const p = prompt.trim();
        if (!p) return this.getFavorites();
        let list = this.getFavorites();
        if (list.includes(p)) list = list.filter((x) => x !== p);
        else list = [p, ...list].slice(0, 20);
        this.writeJson(FAV_KEY, list);
        return list;
    }

    isFavorite(prompt: string): boolean {
        return this.getFavorites().includes(prompt.trim());
    }

    getDetailMode(): SlateDetailMode {
        const v = localStorage.getItem(DETAIL_KEY);
        return v === 'detailed' ? 'detailed' : 'short';
    }

    setDetailMode(mode: SlateDetailMode): void {
        localStorage.setItem(DETAIL_KEY, mode);
    }

    pushHistory(prompt: string): void {
        const p = prompt.trim();
        if (!p) return;
        const hist = this.getHistory().filter((x) => x !== p);
        hist.unshift(p);
        this.writeJson(HIST_KEY, hist.slice(0, 30));
    }

    getHistory(): string[] {
        return this.readJson<string[]>(HIST_KEY, []);
    }

    /** Opt-in local counters — never sends off device. */
    track(event: string, intentId?: string): void {
        try {
            const bag = this.readJson<Record<string, number>>(TEL_KEY, {});
            const key = intentId ? `${event}:${intentId}` : event;
            bag[key] = (bag[key] || 0) + 1;
            this.writeJson(TEL_KEY, bag);
        } catch {
            /* ignore quota */
        }
    }

    private readJson<T>(key: string, fallback: T): T {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return fallback;
            return JSON.parse(raw) as T;
        } catch {
            return fallback;
        }
    }

    private writeJson(key: string, value: unknown): void {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            /* ignore */
        }
    }
}
